import React, { useCallback, useState } from 'react';
import './search-statement-dialog.css';
import { Collapse, TextField } from '@mui/material';
import { useSearchStatementContext } from './search-statement-context';
import EntityDialogSectionHeader from '../../EntityDialogSectionHeader';
import { ChangeSet, ProjectState, StateMutationAction } from '@elyx-code/project-logic-tree';
import { debounce } from '../../../utils';

export interface IEntityMetadataSectionProps {}

const EntityMetadataSection: React.FC<IEntityMetadataSectionProps> = () => {
	const { project, entity } = useSearchStatementContext();

	const [entityName, setEntityName] = useState<string>(entity.name);
	const [entityDescription, setEntityDescription] = useState<string>(
		entity.description
	);

	const [sectionOpen, setSectionOpen] = useState(false);

	const debouncedOnChangeName = useCallback(
		debounce(
			(value: string) => {
				const changeSet = project?.logic?.addChangeSet(
					new ChangeSet(
						project?.logic,
						ProjectState.sessionAuthor,
						new Date().toISOString(),
						entity,
						false,
						StateMutationAction.RenameSearch
					)
				);

				entity?.metaSync({ name: value || null }, changeSet);

				project.renderAndCloseChangeSet(changeSet);
				if (changeSet.has(entity.id)) {
					project.emit(entity.id, {});
				}
			},
			1500,
			false
		),
		[]
	);

	const debouncedOnChangeDescription = useCallback(
		debounce(
			(value: string) => {
				const changeSet = project?.logic?.addChangeSet(
					new ChangeSet(
						project?.logic,
						ProjectState.sessionAuthor,
						new Date().toISOString(),
						entity,
						false,
						StateMutationAction.ChangeSearchDescription
					)
				);

				entity?.metaSync({ description: value || null }, changeSet);

				project.renderAndCloseChangeSet(changeSet);
				if (changeSet.has(entity.id)) {
					project.emit(entity.id, {});
				}
			},
			600,
			false
		),
		[]
	);

	return (
		<section
			style={{
				display: 'flex',
				flexDirection: 'column',
				paddingBottom: sectionOpen ? 20 : 0,
			}}
		>
			<EntityDialogSectionHeader
				title='Name and description'
				subtitle='Add a name and description for this search node in the canvas'
				hideShowSuffix='the name and description sectio'
				open={sectionOpen}
				onToggle={() => setSectionOpen(!sectionOpen)}
			/>

			<Collapse in={sectionOpen}>
				<div
					style={{
						marginTop: 6,
						display: 'flex',
						flexDirection: 'column',
						gap: 12,
						marginLeft: 80,
						paddingRight: 40,
					}}
				>
					<TextField
						label='Search name'
						disabled={!entity.editable}
						variant='outlined'
						style={{ width: '50%' }}
						size='small'
						value={entityName}
						onChange={(e) => {
							setEntityName(e.target.value);
							debouncedOnChangeName(e.target.value);
						}}
					/>

					<TextField
						label='Search description'
						disabled={!entity.editable}
						variant='outlined'
						fullWidth
						multiline
						minRows={3}
						size='small'
						value={entityDescription}
						onChange={(e) => {
							setEntityDescription(e.target.value);
							debouncedOnChangeDescription(e.target.value);
						}}
					/>
				</div>
			</Collapse>
		</section>
	);
};

export default EntityMetadataSection;
